import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export const fetchHRTemplates = async () => {
  const snapshot = await getDocs(collection(db, 'templatesCollections'));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const findTemplateByTitle = (documents, title) =>
  documents.find((d) => (d.catalogKey === title || d.title === title) && d.fileUrl);

export const hasTemplateFile = (documents, title) => !!findTemplateByTitle(documents, title);

/** The template doc itself, matched by catalogKey or title — no file required. */
export const findTemplateDoc = (documents, title) =>
  documents.find((d) => (d.catalogKey || d.title) === title || d.title === title) || null;

/** The doc powering the portal Tailor flow: must have paste-in HTML saved from CRM. */
export const findTailorableTemplate = (documents, title) => {
  const doc = findTemplateDoc(documents, title);
  return doc && (doc.tailorHtml || '').trim() ? doc : null;
};

export const hasTailorHtml = (documents, title) => !!findTailorableTemplate(documents, title);

export const countAvailableTemplates = (documents, titles = []) => {
  const available = titles.filter((title) => hasTemplateFile(documents, title)).length;
  return { available, total: titles.length };
};

export const getHandbookDocument = (documents) => {
  const matches = documents.filter(
    (d) => (d.catalogKey === 'Employee Handbook' || d.title === 'Employee Handbook') && d.fileUrl
  );
  if (matches.length === 0) {
    const similar = documents.filter(
      (d) =>
        d.fileUrl &&
        d.title &&
        (d.title.toLowerCase().includes('handbook') || d.title.toLowerCase().includes('employee'))
    );
    if (similar.length === 0) return null;
    return similar.sort((a, b) => (parseFloat(b.version) || 0) - (parseFloat(a.version) || 0))[0];
  }
  return matches.sort((a, b) => (parseFloat(b.version) || 0) - (parseFloat(a.version) || 0))[0];
};

const OFFICE_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx'];

/**
 * Resolve a URL that VIEWS the file in the browser instead of downloading it.
 * PDFs/TXT render natively; doc/ppt files are routed through the Office web
 * viewer. Spreadsheets return the raw URL — DocumentViewerModal renders them
 * in-app with SheetJS, because the Office viewer decodes the %2F-encoded path
 * separators in Firebase Storage URLs and fails with "File not found".
 */
export const getTemplateViewUrl = (docData) => {
  if (!docData?.fileUrl) return null;
  const ext = (docData.fileName || docData.originalFileName || '')
    .split('.')
    .pop()
    .toLowerCase();
  if (OFFICE_EXTENSIONS.includes(ext)) {
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(docData.fileUrl)}`;
  }
  return docData.fileUrl;
};

/** Open a template in a viewer tab (never a download). Returns false if missing. */
export const viewTemplate = (documents, title) => {
  const docData = findTemplateByTitle(documents, title);
  const url = getTemplateViewUrl(docData);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

export const downloadTemplate = (documents, title) => {
  const doc = findTemplateByTitle(documents, title);
  if (!doc?.fileUrl) return false;
  const link = document.createElement('a');
  link.href = doc.fileUrl;
  link.download = doc.fileName || doc.title;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
};
